import React from 'react';
import { useMobile } from '../../hooks/useMobile';
import { motion } from 'framer-motion';

/**
 * MobileCard - Replaces table rows on mobile devices
 * - Touch-friendly with proper spacing
 * - Supports actions dropdown/swipe
 * - Accessible with proper focus states
 */
const MobileCard = ({ 
  children, 
  onPress, 
  actions = [],
  className = '',
  showBorder = true,
  showShadow = false,
  animated = true
}) => {
  const { isMobile, isTouchDevice } = useMobile();
  
  const baseClasses = `
    ${showBorder ? 'border border-gray-200' : ''}
    ${showShadow ? 'shadow-sm' : ''}
    ${onPress ? 'cursor-pointer' : ''}
    ${isTouchDevice ? 'active:bg-gray-50' : 'hover:bg-gray-50'}
    bg-white rounded-lg transition-colors
  `;

  const CardWrapper = animated ? motion.div : 'div';
  const animationProps = animated ? {
    whileTap: isTouchDevice ? { scale: 0.98 } : undefined,
    transition: { type: 'spring', stiffness: 400, damping: 25 }
  } : {};

  return (
    <CardWrapper
      className={`${baseClasses} ${className}`}
      onClick={onPress}
      {...animationProps}
    >
      {children}
    </CardWrapper>
  );
};

/**
 * MobileCardContent - Structured content for mobile cards
 */
const MobileCardContent = ({ 
  title, 
  subtitle, 
  description, 
  metadata = [],
  badge,
  avatar,
  actions = [],
  children
}) => {
  const { isMobile } = useMobile();
  
  return (
    <div className={`p-4 ${isMobile ? 'min-h-[80px]' : ''}`}>
      <div className="flex items-start justify-between">
        {/* Left content */}
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {/* Avatar */}
          {avatar && (
            <div className="flex-shrink-0">
              {avatar}
            </div>
          )}
          
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Title and badge */}
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {title}
              </h3>
              {badge && (
                <div className="ml-2 flex-shrink-0">
                  {badge}
                </div>
              )}
            </div>
            
            {/* Subtitle */}
            {subtitle && (
              <p className="text-sm text-gray-600 truncate mb-1">
                {subtitle}
              </p>
            )}
            
            {/* Description */}
            {description && (
              <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                {description}
              </p>
            )}
            
            {/* Metadata */}
            {metadata.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                {metadata.map((item, index) => (
                  <span key={index} className="flex items-center">
                    {item.icon && <span className="mr-1">{item.icon}</span>}
                    {item.label}
                  </span>
                ))}
              </div>
            )}
            
            {/* Custom children */}
            {children}
          </div>
        </div>
        
        {/* Actions */}
        {actions.length > 0 && (
          <div className="ml-3 flex-shrink-0">
            <MobileCardActions actions={actions} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * MobileCardActions - Action buttons for mobile cards
 */
const MobileCardActions = ({ actions = [] }) => {
  const { isMobile } = useMobile();
  
  if (actions.length === 0) return null;
  
  // On mobile, show only primary action button + more menu (if there's a primary action)
  if (isMobile && actions.length > 1) {
    const primaryAction = actions.find(action => action.primary);
    const secondaryActions = primaryAction
      ? actions.filter(action => action !== primaryAction)
      : actions;
    
    return (
      <div className="flex items-center space-x-2">
        {/* Primary action - only show if explicitly marked as primary */}
        {primaryAction && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              primaryAction.onClick();
            }}
            className={`
              px-3 py-2 text-xs font-medium rounded-md
              ${primaryAction.variant === 'primary'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
              transition-colors min-h-[44px] min-w-[44px]
            `}
          >
            {primaryAction.icon && <span className="mr-1">{primaryAction.icon}</span>}
            {primaryAction.label}
          </button>
        )}
        
        {/* More actions menu */}
        {secondaryActions.length > 0 && (
          <MobileActionsMenu actions={secondaryActions} />
        )}
      </div>
    );
  }
  
  // Desktop: show all actions inline
  return (
    <div className="flex items-center space-x-2">
      {actions.map((action, index) => (
        <button
          key={index}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
          className={`
            px-2 py-1 text-xs font-medium rounded
            ${action.variant === 'danger' 
              ? 'text-red-600 hover:bg-red-50' 
              : action.variant === 'primary'
              ? 'text-blue-600 hover:bg-blue-50'
              : 'text-gray-600 hover:bg-gray-50'
            }
            transition-colors
          `}
        >
          {action.icon && <span className="mr-1">{action.icon}</span>}
          {action.label}
        </button>
      ))}
    </div>
  );
};

/**
 * MobileActionsMenu - Dropdown menu for secondary actions
 */
const MobileActionsMenu = ({ actions = [] }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
            <div className="py-1">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                    setIsOpen(false);
                  }}
                  className={`
                    w-full text-left px-4 py-3 text-sm font-medium
                    ${action.variant === 'danger' 
                      ? 'text-red-600 hover:bg-red-50' 
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                    transition-colors flex items-center
                  `}
                >
                  {action.icon && <span className="mr-3">{action.icon}</span>}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Export all components
export default MobileCard;
export { MobileCardContent, MobileCardActions, MobileActionsMenu };